import type {
  ChatAttachmentKind,
  ChatMessageType,
  IChatConversationDetail,
  IChatLocalMessage,
  IChatMember,
  IChatMessage,
} from "@/types/(my-space)/chat.interface";

/**
 * Server order by id; optimistic (negative id) messages after every server
 * message, oldest optimistic first. Never by timestamp: two devices' clocks
 * put a message sent after a received one before it within the same minute.
 */
export const compareChatMessages = (a: IChatMessage, b: IChatMessage) => {
  const aPending = a.id < 0;
  const bPending = b.id < 0;
  if (aPending !== bPending) return aPending ? 1 : -1;
  if (aPending) return b.id - a.id;
  return a.id - b.id;
};

/**
 * Insert or replace by server id, or by clientMessageId for our own optimistic
 * copy (the socket echo can beat the HTTP response).
 */
export const upsertMessage = (
  list: IChatLocalMessage[],
  m: IChatLocalMessage,
): IChatLocalMessage[] => {
  const i = list.findIndex(
    (x) =>
      x.id === m.id ||
      (m.clientMessageId != null && x.clientMessageId === m.clientMessageId),
  );
  const next = i === -1 ? [...list, m] : list.map((x, j) => (j === i ? m : x));
  return next.sort(compareChatMessages);
};

/**
 * Who of [members] has read [message] (by read pointer) and who hasn't.
 * Excludes the sender and anyone who joined after it was sent — a joiner's
 * pointer starts at the newest message, which would otherwise count as
 * having read everything before they arrived.
 */
export const splitReadReceipts = (
  members: IChatMember[],
  message: Pick<IChatMessage, "id" | "createdAt">,
  senderId: number | undefined,
) => {
  const read: IChatMember[] = [];
  const unread: IChatMember[] = [];
  const sentAt = new Date(message.createdAt).getTime();
  for (const x of members) {
    if (x.id === senderId) continue;
    if (x.joinedAt && new Date(x.joinedAt).getTime() > sentAt) continue;
    ((x.lastReadMessageId ?? 0) >= message.id ? read : unread).push(x);
  }
  return { read, unread };
};

type ReceiptsChat = Pick<IChatConversationDetail, "type" | "isSelf" | "members">;

/**
 * The "Message info" read/unread split for one of my messages in a group or
 * broadcast, or null where there is none: direct chats (the tick says it all),
 * a self chat (nobody else to read it), unsent copies and others' messages.
 */
export const messageReceipts = (
  chat: ReceiptsChat | null | undefined,
  message: Pick<IChatMessage, "id" | "createdAt" | "sender">,
  myUserId: number | undefined,
) => {
  if (!chat || chat.isSelf || chat.type === "direct" || message.id <= 0) return null;
  if (message.sender?.id !== myUserId || !chat.members.length) return null;
  return splitReadReceipts(chat.members, message, myUserId);
};

/**
 * The double tick: everyone else has read it. Never in a self chat — there is
 * no one else, so my notes keep a single "sent" tick rather than reading as
 * "read by everyone" (an empty "everyone").
 */
export const isReadByOthers = (
  chat: ReceiptsChat | null | undefined,
  message: Pick<IChatMessage, "id" | "createdAt" | "sender">,
  myUserId: number | undefined,
) => {
  if (!chat || chat.isSelf || message.id <= 0) return false;
  const receipts = messageReceipts(chat, message, myUserId);
  if (receipts) return receipts.unread.length === 0 && receipts.read.length > 0;
  const others = chat.members.filter((x) => x.id !== myUserId);
  return others.length > 0 && others.every((x) => (x.lastReadMessageId ?? 0) >= message.id);
};

export const fileKind = (file: { type: string; name: string }): ChatAttachmentKind => {
  const mime = file.type.toLowerCase();
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (["jpg", "jpeg", "png", "webp", "gif", "heic", "heif"].includes(ext)) {
    return "image";
  }
  if (["mp4", "mov", "webm", "m4v"].includes(ext)) return "video";
  return "file";
};

/**
 * A media pick (gallery, camera, drop): all photos → image, anything with a
 * video → video (the API lets a video message carry photos too). Documents
 * are always sent as "file" by the caller.
 */
export const messageTypeForMedia = (files: File[]): ChatMessageType =>
  files.some((f) => fileKind(f) === "video") ? "video" : "image";

export const newClientMessageId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export const mapsUrl = (lat: number, lng: number) =>
  `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

export const isValidHttpUrl = (value: string) => {
  try {
    const u = new URL(value.trim());
    return (u.protocol === "http:" || u.protocol === "https:") && !!u.hostname.includes(".");
  } catch {
    return false;
  }
};

/**
 * The API's catalogue key for a translated error (`chat.scheduled_sending`, …).
 * Branch on this, never on the message — that text follows the language.
 */
export const chatErrorCode = (err: unknown): string | undefined => {
  const code = (err as { data?: { code?: unknown } })?.data?.code;
  return typeof code === "string" ? code : undefined;
};

/** The greeting's part of the day: "morning" before noon, "afternoon" before 18:00, else "evening". */
export const dayPart = (hour: number) =>
  hour < 12 ? ("morning" as const) : hour < 18 ? ("afternoon" as const) : ("evening" as const);
